// Mock Embedded namespace of Beyond Identity SDK temporarilly
const Embedded = {};

const BeyondIdentityUtils = {
    sdkInstance: null,

    buildPasskeyError(message, code) {
        const err = Object.assign(new Error(message), {
            errorCode: code || 'PASSKEY_ERROR',
            errorMessages: [message]
        });

        return err;
    },

    logError(message) {
        if (Sephora?.logger?.error) {
            Sephora.logger.error('[Passkey]', message);
        } else if (Sephora?.logger?.warn) {
            Sephora.logger.warn('[Passkey]', message);
        }
    },

    /**
     * Initialize Beyond Identity SDK
     * @returns {Promise} Resolves with initialized SDK instance
     */
    initialize: async function () {
        if (this.sdkInstance) {
            return this.sdkInstance;
        }

        try {
            const passkeyConfig = Sephora?.configurationSettings?.passkey?.config;

            if (!passkeyConfig) {
                const message = 'Passkey configuration is missing.';
                this.logError(message);

                return Promise.reject(this.buildPasskeyError(message, 'PASSKEY_CONFIG_MISSING'));
            }

            const rawAllowedDomains = passkeyConfig.allowedDomains;
            const normalizedAllowedDomains = Array.isArray(rawAllowedDomains)
                ? rawAllowedDomains.map(domain => (typeof domain === 'string' ? domain.trim().replace(/^"+|"+$/g, '') : domain)).filter(Boolean)
                : rawAllowedDomains;

            const config = {
                // Pass a mutable copy to avoid issues if SDK normalizes/mutates the array
                allowedDomains: Array.isArray(normalizedAllowedDomains) ? [...normalizedAllowedDomains] : normalizedAllowedDomains
            };

            this.sdkInstance = await Embedded.initialize(config);

            return this.sdkInstance;
        } catch (error) {
            const message = `Failed to initialize Beyond Identity SDK: ${error && error.message ? error.message : 'Unknown error'}`;
            this.logError(message);

            return Promise.reject(this.buildPasskeyError(message, 'PASSKEY_INIT_FAILED'));
        }
    },

    /**
     * Get all passkeys registered on this device
     * @returns {Promise<Array>}
     */
    getPasskeys: async function () {
        const sdk = await this.initialize();

        return await sdk.getPasskeys();
    },

    /**
     * Bind a new passkey (register passkey)
     * @param {string} bindingLink - Binding link from backend
     * @returns {Promise<{passkey: Object, postBindingRedirectUri: string}>}
     */
    bindPasskey: async function (bindingLink) {
        const sdk = await this.initialize();

        if (!sdk.isBindPasskeyUrl(bindingLink)) {
            const message = 'Invalid binding link format';
            this.logError(message);

            return Promise.reject(this.buildPasskeyError(message, 'PASSKEY_BIND_INVALID_LINK'));
        }

        return await sdk.bindPasskey(bindingLink);
    },

    /**
     * Authenticate with a passkey
     * @param {string} authenticateUrl - Authentication URL from backend
     * @param {string} credentialId - Optional specific passkey ID
     * @returns {Promise<{redirectUrl: string, message: string, operation: string, passkey: Object}>}
     */
    authenticate: async function (authenticateUrl, credentialId) {
        const sdk = await this.initialize();

        return await sdk.authenticate(authenticateUrl, credentialId);
    },

    /**
     * Delete a passkey
     * @param {string} id - ID of passkey to delete
     * @returns {Promise}
     */
    deletePasskey: async function (id) {
        const sdk = await this.initialize();

        return await sdk.deletePasskey(id);
    }
};

export default BeyondIdentityUtils;
